// ApplicationModal — the proof-of-ownership form for the reseller
// application gate (migration 0052).
//
// Branches on user.applicationStatus:
//
//   pending   → empty form + verification code on display; user fills
//               in channel URL, audience, categories, contact handle,
//               and the post URL containing the code.
//   submitted → waiting state with everything they submitted echoed
//               back read-only + ETA.
//   rejected  → reason banner up top, form re-shown with previous
//               values pre-filled (except verificationPostUrl — new
//               post required because the code was rotated).
//   approved  → modal isn't opened in this state. The banner in App
//               disappears once /me reports approved.
//
// Verification code is shown in a copy-button mono pill. The instructions
// explain to post it as a one-time message in their channel, copy the
// post link, paste it here.

import { useState, useEffect } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Inp, Modal, Mono, Serif } from "./ui.jsx";
import { useT } from "../i18n.jsx";

const AUDIENCE_OPTIONS = [
  { value: "<100",     key: "admin.application.audience.lt100" },
  { value: "100-500",  key: "admin.application.audience.100_500" },
  { value: "500-2k",   key: "admin.application.audience.500_2k" },
  { value: "2k-10k",   key: "admin.application.audience.2k_10k" },
  { value: "10k+",     key: "admin.application.audience.10kp" },
];

// Same set the channel catalog uses, plus a free-text "other". Stored
// as labels in the proof JSON so admin sees them readable; if we ever
// want machine-readable we can swap to slugs without migrating.
//
// Storage is the Russian display label (we record the user's submission
// in the language they saw it in). The `key` is for displaying the same
// option in the user's active locale.
const CATEGORY_OPTIONS = [
  { value: "Женская обувь",          key: "admin.application.category.women.shoes" },
  { value: "Мужская обувь",          key: "admin.application.category.men.shoes" },
  { value: "Женская одежда",         key: "admin.application.category.women.clothing" },
  { value: "Мужская одежда",         key: "admin.application.category.men.clothing" },
  { value: "Детская одежда / обувь", key: "admin.application.category.kids" },
  { value: "Косметика и уход",       key: "admin.application.category.beauty" },
  { value: "Электроника",            key: "admin.application.category.electronics" },
  { value: "Дом и кухня",            key: "admin.application.category.home" },
  { value: "Сумки и аксессуары",     key: "admin.application.category.bags" },
  { value: "Другое",                 key: "admin.application.category.other" },
];

export function ApplicationModal({ user, onClose, onApproved }) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [state, setState] = useState(null);

  // Form fields (only used in pending/rejected states).
  const [channelUrl, setChannelUrl] = useState("");
  const [platform, setPlatform] = useState("telegram");
  const [audienceBand, setAudienceBand] = useState("100-500");
  const [categories, setCategories] = useState([]);
  const [contactHandle, setContactHandle] = useState("");
  const [verificationPostUrl, setVerificationPostUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getApplication();
        setState(r);
        // Pre-fill from previous submission on rejection so the user
        // only has to fix the part the admin flagged.
        if (r.proof) {
          setChannelUrl(r.proof.channelUrl || "");
          setPlatform(r.proof.platform || "telegram");
          setAudienceBand(r.proof.audienceBand || "100-500");
          setCategories(Array.isArray(r.proof.categories) ? r.proof.categories : []);
          setContactHandle(r.proof.contactHandle || "");
          // Deliberately NOT pre-filling verificationPostUrl on reject —
          // the code was rotated, so a new post is needed.
        }
      } catch (e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  const toggleCategory = (cat) => {
    setCategories((cs) =>
      cs.includes(cat) ? cs.filter((c) => c !== cat) : [...cs, cat],
    );
  };

  const copyCode = async () => {
    if (!state?.code) return;
    try {
      await navigator.clipboard.writeText(state.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const submit = async () => {
    setErr(""); setSubmitting(true);
    try {
      await api.submitApplication({
        channelUrl: channelUrl.trim(),
        platform,
        audienceBand,
        categories,
        contactHandle: contactHandle.trim(),
        verificationPostUrl: verificationPostUrl.trim(),
      });
      const fresh = await api.getApplication();
      setState(fresh);
      // Surface in the parent so the banner re-reads /me and switches
      // to "submitted" state without a manual reload.
      if (onApproved) onApproved("submitted");
    } catch (e) { setErr(e.message); }
    setSubmitting(false);
  };

  // ─── Layout helpers ──────────────────────────────────────────────
  const codePill = state?.code ? (
    <div style={{ display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 14px",
                  background: T_NEW.paper, color: T_NEW.ink,
                  border: `1.5px solid ${T_NEW.accent}`,
                  borderRadius: T_NEW.r1, marginBottom: 4 }}>
      <Mono size={18} style={{ letterSpacing: 1.5, fontWeight: 700 }}>
        {state.code}
      </Mono>
      <Btn variant="ghost" onClick={copyCode}
           style={{ marginLeft: "auto", padding: "6px 10px", fontSize: 13 }}>
        {copied ? t("admin.application.copied") : t("admin.application.copy")}
      </Btn>
    </div>
  ) : null;

  const renderForm = () => (
    <>
      <div style={{ padding: 14, background: T_NEW.paperSoft,
                    border: `1px solid ${T_NEW.ink12}`,
                    borderRadius: T_NEW.r2, marginBottom: 16 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56}
              style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
          {t("admin.application.step1.label")}
        </Mono>
        {codePill}
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                      fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                      lineHeight: 1.55, marginTop: 8 }}>
          {t("admin.application.step1.hint")}
        </div>
      </div>

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
        {t("admin.application.step2.label")}
      </Mono>
      <Inp value={channelUrl} onChange={setChannelUrl}
           placeholder="https://t.me/your_channel"
           inputMode="url" autoComplete="off" />

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6 }}>
        {t("admin.application.platform")}
      </Mono>
      <select value={platform} onChange={(e) => setPlatform(e.target.value)}
              style={{ width: "100%", marginBottom: 16,
                       background: T_NEW.paper, color: T_NEW.ink,
                       border: `1px solid ${T_NEW.ink12}`,
                       padding: "12px 14px", borderRadius: T_NEW.r1,
                       fontSize: 15, fontFamily: T_NEW.fontSans }}>
        <option value="telegram">Telegram</option>
        <option value="max">MAX</option>
        <option value="instagram">Instagram</option>
        <option value="vk">{t("admin.application.platform.vk")}</option>
        <option value="ok">{t("admin.application.platform.ok")}</option>
        <option value="website">{t("admin.application.platform.website")}</option>
        <option value="other">{t("admin.application.platform.other")}</option>
      </select>

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6 }}>
        {t("admin.application.audience")}
      </Mono>
      <select value={audienceBand} onChange={(e) => setAudienceBand(e.target.value)}
              style={{ width: "100%", marginBottom: 16,
                       background: T_NEW.paper, color: T_NEW.ink,
                       border: `1px solid ${T_NEW.ink12}`,
                       padding: "12px 14px", borderRadius: T_NEW.r1,
                       fontSize: 15, fontFamily: T_NEW.fontSans }}>
        {AUDIENCE_OPTIONS.map((o) =>
          <option key={o.value} value={o.value}>{t(o.key)}</option>,
        )}
      </select>

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 8 }}>
        {t("admin.application.categories")}
      </Mono>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
        {CATEGORY_OPTIONS.map((cat) => {
          const on = categories.includes(cat.value);
          return (
            <button key={cat.value} type="button" onClick={() => toggleCategory(cat.value)}
                    style={{ padding: "8px 12px", borderRadius: 999,
                             border: `1px solid ${on ? T_NEW.accent : T_NEW.ink12}`,
                             background: on ? `${T_NEW.accent}1A` : T_NEW.paper,
                             color: on ? T_NEW.ink : T_NEW.ink56,
                             fontSize: 13, fontFamily: T_NEW.fontSans,
                             cursor: "pointer" }}>
              {t(cat.key)}
            </button>
          );
        })}
      </div>

      <Inp label={t("admin.application.contact")}
           value={contactHandle} onChange={setContactHandle}
           placeholder="@your_handle" autoComplete="off" />

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 8, marginTop: 8, fontWeight: 600 }}>
        {t("admin.application.step3.label")}
      </Mono>
      <Inp value={verificationPostUrl} onChange={setVerificationPostUrl}
           placeholder="https://t.me/your_channel/123"
           inputMode="url" autoComplete="off" />
      <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                    fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                    lineHeight: 1.55, marginBottom: 16 }}>
        {t("admin.application.step3.hint")}
      </div>

      {err && (
        <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta,
                      marginBottom: 12, padding: "10px 12px",
                      background: `${T_NEW.danger}14`,
                      border: `1px solid ${T_NEW.danger}33`,
                      borderRadius: T_NEW.r1 }}>
          {err}
        </div>
      )}

      <Btn variant="primary" disabled={submitting} onClick={submit}>
        {submitting ? t("admin.application.submitting") : t("admin.application.submit")}
      </Btn>
    </>
  );

  const renderSubmitted = () => (
    <>
      <div style={{ padding: 14, background: `${T_NEW.success}10`,
                    border: `1px solid ${T_NEW.success}44`,
                    borderRadius: T_NEW.r2, marginBottom: 16 }}>
        <Mono size={14} color={T_NEW.ink}
              style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
          {t("admin.application.submitted.title")}
        </Mono>
        <Serif size={13} color={T_NEW.ink56} style={{ lineHeight: 1.55 }}>
          {t("admin.application.submitted.body")}
        </Serif>
      </div>
      {state?.proof && (
        <div style={{ marginBottom: 16 }}>
          <Mono size={T_NEW.micro} color={T_NEW.ink56}
                style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            {t("admin.application.submitted.sent")}
          </Mono>
          <DefList>
            <DefRow k={t("admin.application.field.channel")} v={state.proof.channelUrl} link />
            <DefRow k={t("admin.application.field.audience")} v={state.proof.audienceBand} />
            <DefRow k={t("admin.application.field.categories")} v={(state.proof.categories || []).join(", ")} />
            <DefRow k={t("admin.application.field.contact")} v={state.proof.contactHandle} />
            <DefRow k={t("admin.application.field.verificationPost")} v={state.proof.verificationPostUrl} link />
          </DefList>
        </div>
      )}
    </>
  );

  const renderRejected = () => (
    <>
      <div style={{ padding: 14, background: `${T_NEW.danger}10`,
                    border: `1px solid ${T_NEW.danger}44`,
                    borderRadius: T_NEW.r2, marginBottom: 16 }}>
        <Mono size={14} color={T_NEW.ink}
              style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
          {t("admin.application.rejected.title")}
        </Mono>
        <Serif size={13} color={T_NEW.ink} style={{ lineHeight: 1.55 }}>
          {state?.note || t("admin.application.rejected.noReason")}
        </Serif>
        <div style={{ fontSize: 12, color: T_NEW.ink56, marginTop: 10,
                      fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
          {t("admin.application.rejected.newCode")}
        </div>
      </div>
      {renderForm()}
    </>
  );

  return (
    <Modal title={t("admin.application.title")} onClose={onClose} width={560}>
      {loading
        ? <div style={{ padding: 40, textAlign: "center", color: T_NEW.ink56 }}>
            {t("admin.application.loading")}
          </div>
        : state?.status === "submitted"
          ? renderSubmitted()
          : state?.status === "rejected"
            ? renderRejected()
            : renderForm()
      }
    </Modal>
  );
}

// ─── Tiny inline helpers for the read-only summary ───────────────────
function DefList({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr",
                  gap: "8px 12px", fontSize: 14, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}
function DefRow({ k, v, link }) {
  return (
    <>
      <div style={{ color: T_NEW.ink56, fontFamily: T_NEW.fontMono,
                    fontSize: 12 }}>
        {k}
      </div>
      <div style={{ color: T_NEW.ink, wordBreak: "break-word" }}>
        {link && /^https?:\/\//.test(v)
          ? <a href={v} target="_blank" rel="noopener noreferrer"
               style={{ color: T_NEW.accent }}>{v}</a>
          : v
        }
      </div>
    </>
  );
}
